import Phaser from 'phaser';

/* ========================================================================
 *  Design System – Farben, Typografie, UI-Komponenten
 *  Zentral definiert für konsistentes Design über alle Scenes.
 * ======================================================================== */

export const Colors = {
  bg:         0x060611,
  surface:    0x0e1024,
  surfaceAlt: 0x161838,
  border:     0x1c1e3a,
  borderHi:   0x2a2d55,
  accent:     0x7c5cfc,
  accentLt:   0x9d85ff,
  green:      0x34d399,
  greenDk:    0x1a9c54,
  gold:       0xfbbf24,
  goldDim:    0xb8960b,
  red:        0xf87171,
  redDim:     0xaa2a20,
  text:       0xf0f2ff,
  textHex:    '#f0f2ff',
  textDim:    '#7c82a8',
  textDimHex: '#7c82a8',
  accentHex:  '#7c5cfc',
  greenHex:   '#34d399',
  goldHex:    '#fbbf24',
  redHex:     '#f87171',
  whiteHex:   '#ffffff',
};

export const Fonts = {
  family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  title:  { fontFamily: 'Inter', fontSize: '42px', color: '#ffffff', fontStyle: 'bold' } as Phaser.Types.GameObjects.Text.TextStyle,
  h1:     { fontFamily: 'Inter', fontSize: '30px', color: '#ffffff', fontStyle: 'bold' } as Phaser.Types.GameObjects.Text.TextStyle,
  h2:     { fontFamily: 'Inter', fontSize: '22px', color: '#f0f2ff', fontStyle: '600' } as Phaser.Types.GameObjects.Text.TextStyle,
  h3:     { fontFamily: 'Inter', fontSize: '18px', color: '#f0f2ff', fontStyle: '600' } as Phaser.Types.GameObjects.Text.TextStyle,
  body:   { fontFamily: 'Inter', fontSize: '16px', color: '#f0f2ff' } as Phaser.Types.GameObjects.Text.TextStyle,
  small:  { fontFamily: 'Inter', fontSize: '13px', color: '#7c82a8' } as Phaser.Types.GameObjects.Text.TextStyle,
  label:  { fontFamily: 'Inter', fontSize: '11px', color: '#7c82a8', fontStyle: 'bold', letterSpacing: 2 } as Phaser.Types.GameObjects.Text.TextStyle,
  mono:   { fontFamily: 'Inter', fontSize: '48px', color: '#ffffff', fontStyle: 'bold' } as Phaser.Types.GameObjects.Text.TextStyle,
};

/** Erzeugt einen abgerundeten Button (Rectangle + Text) */
export function createButton(
  scene: Phaser.Scene,
  x: number, y: number,
  w: number, h: number,
  label: string,
  color: number,
  textColor: string,
  onClick: () => void,
): { bg: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text; container: Phaser.GameObjects.Container } {
  const bg = scene.add.rectangle(0, 0, w, h, color)
    .setStrokeStyle(2, lighten(color, 0.2));
  const text = scene.add.text(0, 0, label, {
    fontFamily: 'Inter', fontSize: '16px', color: textColor, fontStyle: '700',
  }).setOrigin(0.5);

  const container = scene.add.container(x, y, [bg, text]);
  bg.setInteractive({ useHandCursor: true });
  bg.on('pointerover', () => bg.setFillStyle(lighten(color, 0.15)));
  bg.on('pointerout', () => bg.setFillStyle(color));
  bg.on('pointerdown', onClick);

  return { bg, text, container };
}

/** Erzeugt ein Pill-Badge */
export function createBadge(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  bgColor: number,
  textColor: string,
): Phaser.GameObjects.Container {
  const text = scene.add.text(0, 0, label, {
    fontFamily: 'Inter', fontSize: '12px', color: textColor, fontStyle: '700',
  }).setOrigin(0.5);
  const pw = text.width + 16;
  const ph = text.height + 8;
  const bg = scene.add.rectangle(0, 0, pw, ph, bgColor).setStrokeStyle(1, lighten(bgColor, 0.2));
  return scene.add.container(x, y, [bg, text]);
}

/** Mitte des aktuellen Bildschirms */
export function center(scene: Phaser.Scene): { cx: number; cy: number; w: number; h: number } {
  const w = scene.cameras.main.width;
  const h = scene.cameras.main.height;
  return { cx: w / 2, cy: h / 2, w, h };
}

/** Hellere Version einer Hex-Farbe */
function lighten(color: number, amount: number): number {
  let r = (color >> 16) & 0xff;
  let g = (color >> 8) & 0xff;
  let b = color & 0xff;
  r = Math.min(255, Math.floor(r + (255 - r) * amount));
  g = Math.min(255, Math.floor(g + (255 - g) * amount));
  b = Math.min(255, Math.floor(b + (255 - b) * amount));
  return (r << 16) | (g << 8) | b;
}
